import { useNavigate } from "react-router-dom";
import ClientLayout from "../../layouts/ClientLayout";
import { useState, useEffect } from "react";
import API, { UPLOADS_URL } from "../../services/api";
import {
  ClipboardList,
  CheckCircle2,
  RefreshCw,
  Clock,
  Calendar,
  Image as ImageIcon,
  Check,
  Ruler,
  SearchX,
  Circle,
  Hourglass,
} from "lucide-react";

// ─── Design System ────────────────────────────────────────────────────────────
const C = {
  violet: "#6366F1",
  violetD: "#4F46E5",
  violetL: "#EEF2FF",
  violetXL: "#F5F3FF",
  indigo: "#4338CA",
  purple: "#7C3AED",
  purpleL: "#F5F3FF",
  green: "#059669",
  greenL: "#ECFDF5",
  greenB: "#6EE7B7",
  orange: "#EA580C",
  orangeL: "#FFF7ED",
  orangeB: "#FED7AA",
  red: "#DC2626",
  redL: "#FEF2F2",
  gray50: "#F9FAFB",
  gray100: "#F3F4F6",
  gray200: "#E5E7EB",
  gray300: "#D1D5DB",
  gray400: "#9CA3AF",
  gray500: "#6B7280",
  gray600: "#4B5563",
  gray700: "#374151",
  gray800: "#1F2937",
  gray900: "#111827",
  white: "#FFFFFF",
  // Shadows
  s1: "0 1px 2px rgba(0,0,0,0.05)",
  s2: "0 1px 3px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)",
  s3: "0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)",
  s4: "0 10px 15px rgba(0,0,0,0.07), 0 4px 6px rgba(0,0,0,0.05)",
};

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS = {
  completed: { label: "Completed", color: C.green, bg: C.greenL, border: C.greenB, dot: C.green, icon: CheckCircle2 },
  inprogress: { label: "In Progress", color: C.orange, bg: C.orangeL, border: C.orangeB, dot: C.orange, icon: Hourglass },
  in_progress: { label: "In Progress", color: C.orange, bg: C.orangeL, border: C.orangeB, dot: C.orange, icon: Hourglass },
  pending: { label: "Pending", color: C.gray500, bg: C.gray100, border: C.gray300, dot: C.gray400, icon: Circle },
};
const getS = (s) => STATUS[s] || STATUS.pending;

// ─── Photo button ─────────────────────────────────────────────────────────────
function PhotoBtn({ label, icon: Icon, variant, onClick }) {
  const [h, setH] = useState(false);
  const v = {
    before: { c: C.red, bg: C.redL, b: "#FECACA" },
    after: { c: C.green, bg: C.greenL, b: C.greenB },
    task: { c: C.purple, bg: C.purpleL, b: "#DDD6FE" },
    default: { c: C.violet, bg: C.violetL, b: "#C7D2FE" },
  }[variant] || { c: C.violet, bg: C.violetL, b: "#C7D2FE" };
  return (
    <button onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "5px 12px", borderRadius: 7,
        border: `1px solid ${h ? v.c : v.b}`,
        background: h ? v.c : v.bg,
        color: h ? "#fff" : v.c,
        fontSize: 11, fontWeight: 600, cursor: "pointer",
        transition: "all 0.14s", whiteSpace: "nowrap",
        boxShadow: h ? `0 2px 8px ${v.c}30` : "none",
      }}>
      {Icon && <Icon className="w-3.5 h-3.5" />}{label}
    </button>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function Badge({ status }) {
  const s = getS(status);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
      textTransform: "uppercase", color: s.color,
      background: s.bg, border: `1px solid ${s.border}`,
      padding: "4px 11px", borderRadius: 20, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
      {s.label}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function ClientTimeline() {
  // ── ALL STATE 100% UNCHANGED ──────────────────────────────────────────────
  const [timelineData, setTimelineData] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [hovIdx, setHovIdx] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projectInfo, setProjectInfo] = useState({
    project_name: "", project_code: "", location: "", start_date: "", progress: 0,
  });
  const navigate = useNavigate();
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  // ── ALL EFFECTS & HANDLERS 100% UNCHANGED ─────────────────────────────────
  useEffect(() => { loadTimeline(); loadProjectInfo(); }, []);

  const loadTimeline = async () => {
    try {
      const projectId = localStorage.getItem("project_id");
      console.log("PROJECT ID =", projectId);
      if (!projectId) { console.error("Project ID not found"); return; }
      const res = await API.get(`/client/project-timeline/${projectId}`);
      console.log("TIMELINE API =", res.data);
      const data = res.data.timeline || [];
      const groupedData = {};
      data.forEach(item => {
        console.log("ITEM DATA =", item);
        const phase = item.phase_id;
        const task = item.task_id;
        // Merge before_photo/after_photo across ALL rows in the phase,
        // not just the first one encountered, so a photo uploaded on any
        // task still surfaces at the phase level.
        if (!groupedData[phase]) {
          groupedData[phase] = {
            phase_name: phase, status: item.status,
            before_photo: item.before_photo || null,
            after_photo: item.after_photo || null,
            tasks: {}
          };
        } else {
          if (!groupedData[phase].before_photo && item.before_photo) {
            groupedData[phase].before_photo = item.before_photo;
          }
          if (!groupedData[phase].after_photo && item.after_photo) {
            groupedData[phase].after_photo = item.after_photo;
          }
        }
        if (!groupedData[phase].tasks[task]) {
          groupedData[phase].tasks[task] = { task_name: task, task_image: item.task_image, updates: [] };
        }
        groupedData[phase].tasks[task].updates.push({
          subtask_name: item.subtask_name,
          comment: item.comment,
          measurement: item.measurement,
          progress_date: item.progress_date,
          progress_image: (() => {
            if (!item.progress_image) return [];
            try { return JSON.parse(item.progress_image); } catch { return [item.progress_image]; }
          })(),
          status: item.status,
        });
      });
      setTimelineData(Object.values(groupedData));
      console.log("TIMELINE DATA =", data);
    } catch (err) { console.error("Timeline Error:", err); }
  };

  const loadProjectInfo = async () => {
    try {
      const projectId = localStorage.getItem("project_id");
      const res = await API.get(`/client/project-details/${projectId}`);
      console.log("PROJECT INFO =", res.data);
      setProjectInfo(res.data.project);
    } catch (err) { console.error("Project Info Error:", err); }
  };

  // ── ALL DERIVED VALUES 100% UNCHANGED ────────────────────────────────────
  const completed = timelineData.filter(t => t.status === "completed").length;
  const inprogress = timelineData.filter(t => t.status === "in_progress" || t.status === "inprogress").length;
  const pending = timelineData.filter(t => t.status === "pending").length;
  const pct = timelineData.length > 0 ? Math.round((completed / timelineData.length) * 100) : 0;

const filtered = activeFilter === "All"
    ? timelineData
    : activeFilter === "In Progress"
      ? timelineData.filter(t => t.status === "in_progress" || t.status === "inprogress")
      : activeFilter === "Completed"
        ? timelineData.filter(t => t.status === "completed")
        : timelineData.filter(t => t.status === "pending");
  // ── END UNCHANGED ─────────────────────────────────────────────────────────

  // ── NEW HELPER: strip any folder path before comparing filenames ─────────
  const stripPath = (f) => (f || "").split("/").pop();

  return (
    <ClientLayout>
      <style>{`
       @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

        .ct * { font-family: 'Montserrat', -apple-system, sans-serif !important; box-sizing: border-box; }
        .ct-mono { font-family: 'Montserrat', sans-serif !important; }
        
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: ${C.gray300}; border-radius: 10px; }

        /* Phase card hover */
        .ct-phase {
          transition: box-shadow 0.18s, border-color 0.18s;
        }
        .ct-phase:hover {
          box-shadow: ${C.s4} !important;
          border-color: ${C.gray300} !important;
        }

        /* Filter buttons */
        .ct-f { transition: all 0.13s; cursor: pointer; outline: none; }
        .ct-f:hover { opacity: 0.80; }

        /* Progress bar fill animation */
        @keyframes ct-fill {
          from { width: 0; }
          to   { width: ${pct}%; }
        }
        .ct-fill {
          animation: ct-fill 1.0s cubic-bezier(0.4,0,0.2,1) forwards;
          animation-delay: 0.3s;
          width: 0;
        }

        /* Orb pulse for in-progress */
        @keyframes ct-pulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(234,88,12,0.35); }
          50%      { box-shadow: 0 0 0 5px rgba(234,88,12,0); }
        }
        .ct-pulse { animation: ct-pulse 2s ease-in-out infinite; }

        /* Card appear */
        @keyframes ct-in {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:none; }
        }
        .ct-in { animation: ct-in 0.3s ease both; }

        @media (max-width: 900px) {
          .ct-stat-grid  { grid-template-columns: repeat(2,1fr) !important; }
          .ct-proj-row   { flex-direction: column !important; gap: 16px !important; }
          .ct-ph-hdr     { flex-wrap: wrap !important; gap: 8px !important; }
          .ct-filters    { flex-wrap: wrap !important; }
          .ct-pad        { padding: 16px !important; }
        }
        @media (max-width: 480px) {
          .ct-stat-grid  { grid-template-columns: 1fr 1fr !important; }
          .ct-upd-row    { flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>

      <div className="ct ct-pad" style={{ padding: "24px 28px", background: C.gray50, minHeight: "100vh", display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ══════════════════════════════════════════════════════════
            PROJECT INFO CARD
        ══════════════════════════════════════════════════════════ */}
        <div className="ct-in" style={{
          background: C.white, borderRadius: 16,
          border: `1px solid ${C.gray200}`,
          boxShadow: C.s3, overflow: "hidden",
        }}>
          {/* Coloured top stripe */}
          <div style={{ height: 4, background: `linear-gradient(90deg, ${C.violet}, ${C.purple}, ${C.orange})` }} />

          <div className="ct-proj-row" style={{ padding: "22px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>

            {/* Left — identity */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, minWidth: 0 }}>
              {/* Avatar */}
              <div style={{
                width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                background: `linear-gradient(135deg, ${C.violet}, ${C.purple})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, fontWeight: 800, color: C.white,
                boxShadow: `0 4px 14px rgba(99,102,241,0.35)`,
              }}>
                {(projectInfo?.title || projectInfo?.project_name || "P").charAt(0).toUpperCase()}
              </div>

              <div style={{ minWidth: 0 }}>
                {/* UID + Active */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span className="ct-mono" style={{ fontSize: 11, fontWeight: 500, color: C.gray400, letterSpacing: "0.08em" }}>
                    {projectInfo?.project_uid || projectInfo?.project_code || "—"}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: "0.10em",
                    textTransform: "uppercase", color: C.green,
                    background: C.greenL, border: `1px solid ${C.greenB}`,
                    padding: "2px 9px", borderRadius: 20,
                  }}>Active</span>
                </div>
<div style={{ fontWeight: 700, fontSize: 18, color: C.gray900, lineHeight: 1.2, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {projectInfo?.title || projectInfo?.project_name || "Project Name"}
                </div>
                <div style={{ fontSize: 12, color: C.gray400, fontWeight: 400, display: "flex", alignItems: "center", gap: 5 }}>
                  <Calendar className="w-3.5 h-3.5" /> Started {projectInfo?.start_date
                    ? new Date(projectInfo.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                    : "—"}
                </div>
              </div>
            </div>

            {/* Right — progress */}
            <div style={{ minWidth: 220 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.gray500 }}>Overall Progress</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: C.violet, letterSpacing: "-0.5px" }}>{pct}%</span>
              </div>
              {/* Progress bar */}
              <div style={{ height: 10, background: C.gray100, borderRadius: 20, overflow: "hidden", position: "relative" }}>
                <div className="ct-fill" style={{
                  height: "100%", borderRadius: 20,
                  background: `linear-gradient(90deg, ${C.violet}, ${C.purple})`,
                  boxShadow: `0 2px 6px rgba(99,102,241,0.40)`,
                }} />
              </div>
              <div style={{ fontSize: 12, color: C.gray400, marginTop: 6, textAlign: "right" }}>
                {completed} of {timelineData.length} phases complete
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════
            KPI STAT CARDS — single row, no duplication
        ══════════════════════════════════════════════════════════ */}
        <div className="ct-stat-grid ct-in" style={{
          display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14,
          animationDelay: "0.05s",
        }}>
          {[
            { label: "Total Phases", val: timelineData.length, icon: ClipboardList, color: C.violet, bg: C.violetL, border: "#C7D2FE", iconBg: C.violetD },
            { label: "Completed", val: completed, icon: CheckCircle2, color: C.green, bg: C.greenL, border: C.greenB, iconBg: C.green },
            { label: "In Progress", val: inprogress, icon: RefreshCw, color: C.orange, bg: C.orangeL, border: C.orangeB, iconBg: C.orange },
            { label: "Pending", val: pending, icon: Clock, color: C.gray500, bg: C.gray100, border: C.gray300, iconBg: C.gray500 },
          ].map((s, i) => (
            <div key={s.label} style={{
              background: C.white, borderRadius: 14,
              border: `1px solid ${s.border}`,
              padding: "18px 20px",
              boxShadow: C.s2,
              display: "flex", alignItems: "center", gap: 14,
              position: "relative", overflow: "hidden",
            }}>
              {/* Faint circle bg */}
              <div style={{ position: "absolute", right: -14, top: -14, width: 70, height: 70, borderRadius: "50%", background: s.bg, opacity: 0.6 }} />
              {/* Icon box */}
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: s.bg, border: `1.5px solid ${s.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, position: "relative", color: s.color,
              }}><s.icon className="w-5 h-5" /></div>
              <div style={{ position: "relative" }}>
                <div style={{ fontWeight: 800, fontSize: 30, color: s.color, lineHeight: 1, letterSpacing: "-1px" }}>{s.val}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.gray500, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════
            TIMELINE SECTION — the hero
        ══════════════════════════════════════════════════════════ */}
        <div className="ct-in" style={{
          background: C.white, borderRadius: 16,
          border: `1px solid ${C.gray200}`,
          boxShadow: C.s3, overflow: "hidden",
          animationDelay: "0.10s",
        }}>
          {/* Top stripe */}
          <div style={{ height: 3, background: `linear-gradient(90deg, ${C.violet}, ${C.orange})` }} />

          {/* Toolbar */}
          <div style={{ padding: "20px 24px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.gray900 }}>Project Phases</div>
                <div style={{ fontSize: 12, color: C.gray400, marginTop: 3 }}>
                  {filtered.length} of {timelineData.length} phases visible
                </div>
              </div>

              {/* Filter pills */}
              <div className="ct-filters" style={{ display: "flex", gap: 8 }}>
                {["All", "Completed", "In Progress", "Pending"].map(f => {
                  const active = activeFilter === f;
                  const fc = {
                    "All": { c: C.violet, bg: C.violetL, border: "#C7D2FE" },
                    "Completed": { c: C.green, bg: C.greenL, border: C.greenB },
                    "In Progress": { c: C.orange, bg: C.orangeL, border: C.orangeB },
                    "Pending": { c: C.gray600, bg: C.gray100, border: C.gray300 },
                  }[f];
                  return (
                    <button key={f} className="ct-f" onClick={() => setActiveFilter(f)} style={{
                      fontSize: 12, fontWeight: active ? 700 : 500,
                      padding: "7px 18px", borderRadius: 8,
                      border: `1.5px solid ${active ? fc.border : C.gray200}`,
                      background: active ? fc.bg : "transparent",
                      color: active ? fc.c : C.gray500,
                      boxShadow: active ? C.s1 : "none",
                    }}>{f}</button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Phase entries */}
          <div style={{ padding: "0 24px 28px", position: "relative" }}>
            {/* Vertical rail */}
            <div style={{
              position: "absolute", left: 44, top: 0, bottom: 28,
              width: 2, background: C.gray200,
              borderRadius: 10, zIndex: 0,
            }} />

            {filtered.length === 0 ? (
              <div style={{ padding: "64px 0", textAlign: "center" }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, color: C.gray300 }}><SearchX className="w-12 h-12" /></div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.gray700, marginBottom: 6 }}>No phases match this filter</div>
                <div style={{ fontSize: 13, color: C.gray400 }}>Try selecting a different status above.</div>
              </div>
            ) : filtered.map((phase, i) => {
              const sm = getS(phase.status);
              const phaseN = timelineData.indexOf(phase) + 1;
              const isIP = phase.status === "in_progress" || phase.status === "inprogress";

              return (
                <div key={i}
                  style={{ display: "flex", gap: 20, marginBottom: i < filtered.length - 1 ? 16 : 0, position: "relative", zIndex: 1 }}
                  onMouseEnter={() => setHovIdx(i)}
                  onMouseLeave={() => setHovIdx(null)}
                >
                  {/* Orb */}
                  <div style={{ flexShrink: 0, paddingTop: 18, zIndex: 2 }}>
                    <div className={isIP ? "ct-pulse" : ""} style={{
                      width: 40, height: 40, borderRadius: "50%",
                      background: `linear-gradient(135deg, ${C.gray900}, #1F2937)`,
                      border: `2.5px solid ${sm.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, fontWeight: 800, color: "#fff",
                      transition: "transform 0.18s",
                      transform: hovIdx === i ? "scale(1.12)" : "scale(1)",
                      boxShadow: `0 4px 12px rgba(17,24,39,0.35)`,
                    }}>
                      {String(phaseN).padStart(2, "0")}
                    </div>
                  </div>

                  {/* Card */}
                  <div className="ct-phase" style={{
                    flex: 1, minWidth: 0,
                    borderRadius: 14,
                    border: `1px solid ${C.gray200}`,
                    background: C.white,
                    boxShadow: C.s2,
                    overflow: "hidden",
                  }}>
                    {/* Phase header — Before photo only, After photo moved below */}
                    <div style={{
                      padding: "14px 20px",
                      background: C.gray50,
                      borderBottom: `1px solid ${C.gray100}`,
                    }}>
                      <div className="ct-ph-hdr" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                        {/* Left */}
                       <div style={{ display: "flex", alignItems: "center", gap: 50, flexWrap: "wrap" }}>
  <div style={{ display: "flex", gap: 580 }}>
    {phase.before_photo && (
      <div
        onClick={() => {
          setSelectedImages([phase.before_photo]);
          setShowImageModal(true);
        }}
        style={{
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginLeft: 10,
        }}
      >
        <img
          src={`${UPLOADS_URL}${phase.before_photo}`}
          alt="Before"
          style={{
            width: 160,
            height: 120,
            objectFit: "cover",
            borderRadius: 10,
            border: "1px solid #E5E7EB",
          }}
        />
        <span
          style={{
            marginTop: 6,
            fontSize: 15,
            fontWeight: 700,
            color: "#DC2626",
          }}
        >
          Before
        </span>
      </div>
    )}
  </div>
  <span style={{ fontWeight: 700, fontSize: 14, color: C.gray900 }}>
    {phase.phase_name}
  </span>
</div>
                      </div>
                    </div>

                    {/* Task blocks */}
                    <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                      {Object.entries(phase.tasks || {}).map(([taskName, task], tIdx) => (
                        <div key={tIdx} style={{
                          border: `1px solid ${C.gray200}`,
                          borderRadius: 10, overflow: "hidden",
                          background: C.white,
                        }}>
                          {/* Task header strip */}
                          <div style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 16px",
                            background: C.violetXL,
                            borderBottom: `1px solid #DDD6FE`,
                            flexWrap: "wrap",
                          }}>
                            <div style={{
                              width: 22, height: 22, borderRadius: 6,
                              background: C.violet, color: "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}><Check className="w-3.5 h-3.5" /></div>
                            <span style={{ fontWeight: 700, fontSize: 13, color: C.gray900, flex: 1, minWidth: 0 }}>
                              {taskName}
                            </span>
                            {task.task_image && (
                              <PhotoBtn label="Task Image" icon={ImageIcon} variant="task"
                                onClick={() => { setSelectedImages([task.task_image]); setShowImageModal(true); }} />
                            )}
                          </div>
                          {/* Updates */}
                          <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                            {task.updates.filter(upd => {
                              // NEW: hide auto-generated "Schedule created/updated" noise entries
                              const isAutoLog = /^schedule (created|updated)$/i.test((upd.comment || "").trim());
                              if (isAutoLog) return false;

                              if (!upd.progress_image || upd.progress_image.length === 0) return true;
                              const afterName = stripPath(phase.after_photo);
                              const beforeName = stripPath(phase.before_photo);
                              return !upd.progress_image.some(img => {
                                const imgName = stripPath(img);
                                return imgName === afterName || imgName === beforeName;
                              });
                            }).map((upd, uIdx) => (
                              <div key={uIdx} style={{
                                background: C.gray50,
                                border: `1px solid ${C.gray100}`,
                                borderRadius: 10,
                                padding: "12px 14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                              }}>
                                {/* Left side */}
                                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                                  {/* Dot */}
                                  <div style={{
                                    width: 8, height: 8, borderRadius: "50%",
                                    background: C.violet, flexShrink: 0,
                                  }} />
                                  <div style={{ minWidth: 0 }}>
                                    {upd.subtask_name && (
                                      <div style={{ fontSize: 12, fontWeight: 700, color: C.gray700, marginBottom: 2 }}>
                                        {upd.subtask_name}
                                      </div>
                                    )}
                                    {upd.comment && (
                                      <div style={{ fontSize: 13, color: C.gray600, lineHeight: 1.5 }}>
                                        {upd.comment}
                                      </div>
                                    )}
                                    {upd.measurement && (
                                      <span style={{
                                        display: "inline-flex", alignItems: "center", gap: 4,
                                        fontSize: 11, fontWeight: 700, color: C.violet,
                                        background: C.violetL, border: "1px solid #C7D2FE",
                                        padding: "2px 8px", borderRadius: 5, marginTop: 4,
                                      }}>
                                        <Ruler className="w-3 h-3" /> {upd.measurement}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Right side: image + date */}
                                {upd.progress_image?.length > 0 && (
                                  <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>
                                    {upd.progress_date && (
                                      <span style={{ fontSize: 11, color: C.gray400, fontWeight: 500, whiteSpace: "nowrap" }}>
                                        {new Date(upd.progress_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                                      </span>
                                    )}
                                    <img
                                     src={`${UPLOADS_URL}${upd.progress_image[0]}`}
                                      alt="Progress"
                                      onClick={() => { setSelectedImages(upd.progress_image); setShowImageModal(true); }}
                                      style={{
                                        width: 72, height: 56, objectFit: "cover",
                                        borderRadius: 8, border: `1px solid ${C.gray200}`,
                                        cursor: "pointer", flexShrink: 0,
                                        transition: "transform 0.15s",
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                                    />
                                  </div>
                                )}

                                {/* Date only (no image) */}
                                {(!upd.progress_image || upd.progress_image.length === 0) && upd.progress_date && (
                                  <span style={{ fontSize: 11, color: C.gray400, fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0 }}>
                                    {new Date(upd.progress_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
{/* ── After photo — pinned to left edge of card ── */}
{phase.after_photo && (
  <div style={{
    padding: "16px 20px 20px",
    borderTop: `1px solid ${C.gray100}`,
    display: "flex",
  }}>
    <div
      onClick={() => { setSelectedImages([phase.after_photo]); setShowImageModal(true); }}
      style={{
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
                          <img
                            src={`${UPLOADS_URL}${phase.after_photo}`}
                            alt="After"
                            style={{
                              width: 160,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 10,
                              border: "1px solid #E5E7EB",
                            }}
                          />
                          <span
                            style={{
                              marginTop: 6,
                              fontSize: 15,
                              fontWeight: 700,
                              color: "#059669",
                            }}
                          >
                            After
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* IMAGE MODAL */}

          {/* ══════════════════════════════════════════════════════════
          IMAGE MODAL — logic unchanged
                  ══════════════════════════════════════════════════════════ */}
          {showImageModal && (
            <div onClick={() => setShowImageModal(false)} style={{
              position: "fixed", inset: 0, zIndex: 9999,
              background: "rgba(17,24,39,0.80)", backdropFilter: "blur(8px)",
              display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
            }}>
              <div onClick={e => e.stopPropagation()} style={{
                background: C.white, borderRadius: 18, padding: "28px",
                maxWidth: 900, width: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.40)",
                maxHeight: "90vh", overflowY: "auto", position: "relative",
              }}>
                {/* Close X */}
                <button onClick={() => setShowImageModal(false)} style={{
                  position: "absolute", top: 16, right: 16,
                  width: 34, height: 34, borderRadius: 8,
                  background: C.gray100, border: `1px solid ${C.gray200}`,
                  color: C.gray600, fontSize: 18, fontWeight: 700,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>×</button>

                <div style={{ fontWeight: 700, fontSize: 15, color: C.gray900, marginBottom: 18, paddingRight: 44 }}>
                  Project Photos
                  <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: C.gray400 }}>
                    {selectedImages.length} {selectedImages.length === 1 ? "image" : "images"}
                  </span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12 }}>
                  {selectedImages.map((img, idx) => (
                    <img key={idx}
                      src={`${UPLOADS_URL}${img}`}
                      alt=""
                      style={{ width: "100%", borderRadius: 10, display: "block", border: `1px solid ${C.gray200}` }}
                    />
                  ))}
                </div>

                <div style={{ textAlign: "center", marginTop: 22 }}>
                  <button onClick={() => setShowImageModal(false)} style={{
                    padding: "10px 32px", borderRadius: 10,
                    background: `linear-gradient(135deg,${C.violet},${C.purple})`,
                    color: "#fff", fontSize: 13, fontWeight: 700,
                    border: "none", cursor: "pointer",
                    boxShadow: `0 4px 14px rgba(99,102,241,0.35)`,
                  }}>Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ClientLayout >
  );
}